import { liveQuery } from "dexie";
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

import type { PosSaleMember } from "../runtime/gateway/sale-context-client";
import {
  localCartSaleFingerprint,
  setLocalCartMember,
  setLocalCartPromotionClaims,
  setLocalCartQuote,
} from "../runtime/local-store/cart-state";
import {
  currentCartRuntimeScopeKey,
  readCurrentCartRuntime,
  subscribeCurrentCartRuntime,
} from "../runtime/local-store/current-cart-runtime";
import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import { normalizeIdentifier } from "../runtime/local-store/indexed-runtime";
import { searchIndexedMembers } from "../runtime/local-store/member-runtime";
import { signupPosMember } from "../runtime/local-store/member-signup-state";
import { refreshLocalSaleQuote } from "../runtime/local-store/sale-context-state";
import { readTerminalCredential, type TerminalCredentialState } from "../runtime/local-store/terminal-state";
import { resolveLocalCustomerClaimPromotionsV1 } from "../runtime/promotion/local-customer-claim-resolver";
import {
  clearPromotionClaimSession,
  readPromotionClaimSession,
  setPromotionClaimCredential,
  subscribePromotionClaimSession,
} from "../runtime/promotion/promotion-claim-session";
import { resolveLocalPromotionsV1 } from "../runtime/promotion/local-promotion-resolver";
import { usePosPromotionRuntime } from "../runtime/promotion/use-pos-promotion-runtime";

type RuntimeSnapshot = {
  terminal: TerminalCredentialState | null;
  session: LocalEmployeeSessionState | null;
};

function money(value: number, currency = "THB") {
  return new Intl.NumberFormat("th-TH", { style: "currency", currency, minimumFractionDigits: 2 }).format(value);
}

function phoneLast4(value: string | null) {
  if (!value) return "";
  return value.replace(/\D/g, "").slice(-4);
}

function claimReason(value?: string | null) {
  if (!value) return "ยังไม่ผ่านเงื่อนไขของโปรโมชั่น";
  if (value.includes("PROMOTION_CLAIM_CODE_NOT_FOUND")) return "ไม่พบรหัสคูปองนี้";
  if (value.includes("PROMOTION_CLAIM_EXPIRED") || value.includes("EXPIRED")) return "คูปองหมดอายุแล้ว";
  if (value.includes("PROMOTION_CLAIM_CONSUMED") || value.includes("CONSUMED")) return "คูปองนี้ถูกใช้ไปแล้ว";
  if (value.includes("BRANCH_NOT_ELIGIBLE")) return "คูปองนี้ใช้ไม่ได้ที่สาขานี้";
  if (value.includes("CHANNEL_NOT_ELIGIBLE")) return "คูปองนี้ใช้ไม่ได้กับช่องทางขายนี้";
  if (value.includes("CONDITION_NOT_MET")) return "บิลนี้ยังไม่เข้าเงื่อนไขของคูปอง";
  if (value.includes("NO_ELIGIBLE_AMOUNT")) return "ไม่มียอดที่เข้าเงื่อนไขให้ใช้คูปอง";
  if (value.includes("CONFLICT") || value.includes("LOST")) return "คูปองถูกโปรโมชั่นอื่นที่เหมาะสมกว่าชนะในบิลนี้";
  return value;
}

function friendlyError(value: string) {
  if (value.includes("GATEWAY_UNREACHABLE") || value.includes("GATEWAY_TIMEOUT")) return "ติดต่อ Commerce Core ไม่ได้ กรุณาตรวจอินเทอร์เน็ต";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) return "Session พนักงานหมดอายุ กรุณา Login ใหม่";
  if (value.includes("STAFF_CLAIM_PROMOTION_NOT_AVAILABLE")) return "สิทธิ์โปรโมชั่นนี้ไม่พร้อมใช้แล้ว ระบบยกเลิกการเลือกให้ตรวจใหม่";
  if (value.includes("PROMOTION_MANUAL_SELECTION_LIMIT_EXCEEDED")) return "เลือกสิทธิ์ลูกค้าได้ครั้งละ 1 สิทธิ์ กรุณาเอาสิทธิ์เดิมออกหรือเลือกสิทธิ์ใหม่แทน";
  if (value.includes("ACTIVE_MEMBER_REQUIRED")) return "โปรโมชั่นนี้เป็นสิทธิ์สมาชิก กรุณาเลือกสมาชิกก่อน";
  if (value.includes("POS_SALE_QUOTE_CART_CHANGED")) return "บิลเปลี่ยนระหว่างตรวจสิทธิ์ ระบบจะตรวจใหม่";
  if (value.includes("POS_SALE_CLAIM_CHANGED")) return "รหัสคูปองเปลี่ยนระหว่างตรวจ ระบบจะใช้รหัสล่าสุดเท่านั้น";
  if (value.includes("PROMOTION_CLAIM")) return claimReason(value);
  return value || "อ่านบริบทการขายไม่สำเร็จ";
}

function memberSignupError(value: string) {
  if (value.includes("CUSTOMER_MEMBER_SIGNUP_PERMISSION_REQUIRED")) return "บัญชีพนักงานนี้ไม่มีสิทธิ์สมัครสมาชิกหน้าร้าน";
  if (value.includes("CUSTOMER_MEMBER_SIGNUP_POLICY_NOT_READY")) return "นโยบายสมัครสมาชิกยังไม่พร้อม กรุณาตรวจใน Back Office";
  if (value.includes("PHONE_IDENTIFIER_AMBIGUOUS_REQUIRES_BACK_OFFICE_REVIEW")) return "เบอร์นี้ผูกกับลูกค้ามากกว่าหนึ่งราย กรุณาตรวจใน Back Office ก่อน";
  if (value.includes("EXISTING_CUSTOMER_REQUIRES_BACK_OFFICE_REVIEW")) return "พบข้อมูลลูกค้าเดิมที่ต้องตรวจสถานะใน Back Office ก่อนสมัครสมาชิก";
  if (value.includes("MEMBERSHIP_TERMS_ACCEPTANCE_REQUIRED")) return "กรุณายืนยันว่าลูกค้ารับทราบเงื่อนไขสมาชิกและความเป็นส่วนตัว";
  if (value.includes("PHONE_FORMAT_INVALID")) return "กรุณากรอกเบอร์โทรให้ถูกต้อง";
  if (value.includes("NICKNAME_REQUIRED")) return "กรุณากรอกชื่อเล่น";
  if (value.includes("GATEWAY_UNREACHABLE") || value.includes("GATEWAY_TIMEOUT")) return "สมัครสมาชิกใหม่ต้อง Online กรุณาตรวจอินเทอร์เน็ต";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) return "Session พนักงานหมดอายุ กรุณา Login ใหม่";
  return value || "สมัครสมาชิกไม่สำเร็จ กรุณาลองใหม่";
}

export function SaleContextRuntimeHost() {
  const [runtime, setRuntime] = useState<RuntimeSnapshot>({ terminal: null, session: null });
  const [mountNode, setMountNode] = useState<Element | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [memberOpen, setMemberOpen] = useState(false);
  const [memberSignupOpen, setMemberSignupOpen] = useState(false);
  const [promotionOpen, setPromotionOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [members, setMembers] = useState<PosSaleMember[]>([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [signupNickname, setSignupNickname] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupTermsAccepted, setSignupTermsAccepted] = useState(false);
  const [signupBusy, setSignupBusy] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [couponInput, setCouponInput] = useState("");
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const promotionRuntime = usePosPromotionRuntime(runtime.terminal, online);
  const claimSession = useSyncExternalStore(
    subscribePromotionClaimSession,
    readPromotionClaimSession,
    readPromotionClaimSession,
  );
  const currentCartRuntime = useSyncExternalStore(
    subscribeCurrentCartRuntime,
    readCurrentCartRuntime,
    readCurrentCartRuntime,
  );

  useEffect(() => {
    const subscription = liveQuery(async () => {
      const [terminal, session] = await Promise.all([
        readTerminalCredential(),
        readLocalEmployeeSession(),
      ]);
      return { terminal, session } satisfies RuntimeSnapshot;
    }).subscribe({ next: setRuntime, error: () => setRuntime({ terminal: null, session: null }) });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const updateMount = () => setMountNode(document.querySelector(".sale-context-mount"));
    updateMount();
    const observer = new MutationObserver(updateMount);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => { window.removeEventListener("online", update); window.removeEventListener("offline", update); };
  }, []);

  useEffect(() => {
    if (!claimSession.credentialCode) setCouponInput("");
  }, [claimSession.credentialCode]);

  const terminal = runtime.terminal;
  const identity = terminal?.identity ?? null;
  const currentScopeKey = identity ? currentCartRuntimeScopeKey(identity) : null;
  const cart = currentScopeKey && currentCartRuntime.scopeKey === currentScopeKey
    ? currentCartRuntime.cart
    : null;
  const session = runtime.session;
  const actor = session?.actor ?? null;
  const canSignupMember = actor?.permission_codes.includes("customer.member.signup") ?? false;
  const cartFingerprint = cart ? localCartSaleFingerprint(cart) : "";
  const quoteFresh = Boolean(cart?.quote && cart.quote.cartFingerprint === cartFingerprint);
  const quote = quoteFresh ? cart?.quote ?? null : null;
  const sameScope = Boolean(actor && identity
    && actor.organization_id === identity.organization_id
    && actor.branch_id === identity.branch_id
    && actor.node_id === identity.node_id
    && actor.runtime_session_status === "ACTIVE");

  const refreshServerQuote = useCallback(async () => {
    if (!online || !identity || !terminal?.deviceSecret || !session?.sessionToken || !cart?.lines.length) return;
    setQuoteBusy(true);
    setQuoteError(null);
    try {
      await refreshLocalSaleQuote({
        identity,
        deviceSecret: terminal.deviceSecret,
        sessionToken: session.sessionToken,
        cart,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "POS_SALE_QUOTE_FAILED";
      if (!message.includes("POS_SALE_QUOTE_CART_CHANGED")) setQuoteError(friendlyError(message));
    } finally {
      setQuoteBusy(false);
    }
  }, [cart, identity, online, session?.sessionToken, terminal?.deviceSecret]);

  useEffect(() => {
    if (!sameScope || !identity || !cart?.lines.length) return;
    if (promotionRuntime.status === "loading" || promotionRuntime.status === "error") return;

    const localQuoteFresh = Boolean(
      quoteFresh
      && cart.quote?.quoteSource === "LOCAL_PROMOTION_RUNTIME_V1"
      && cart.quote.promotionRuntimeRevision === promotionRuntime.runtime.revision,
    );
    const serverQuoteFresh = Boolean(quoteFresh && cart.quote?.quoteSource === "SERVER_COMMERCE_QUOTE_V1");

    if (claimSession.customerClaim) {
      const customerClaimServerQuoteFresh = Boolean(
        serverQuoteFresh
        && claimSession.evaluation?.claim_instance_id === claimSession.customerClaim.claimInstanceId,
      );
      // A fresh canonical readback for this exact Claim Evidence is terminal for the
      // current cart fingerprint. Do not let another Claim inherit a stale server quote.
      if (customerClaimServerQuoteFresh) return;

      let resolution;
      try {
        resolution = resolveLocalCustomerClaimPromotionsV1({
          cart,
          promotions: promotionRuntime.runtime.promotions,
          promotionRuntimeRevision: promotionRuntime.runtime.revision,
          claimPromotionVersionId: claimSession.customerClaim.promotionVersionId,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : "PROMOTION_CLAIM_LOCAL_QUOTE_FAILED";
        if (!message.includes("PROMOTION_CLAIM_LOCAL_PROOF_NOT_READY")) {
          setQuoteError(friendlyError(message));
          return;
        }
        if (online && !quoteBusy) void refreshServerQuote();
        return;
      }

      const customerClaimLocalQuoteFresh = Boolean(
        localQuoteFresh
        && cart.quote?.quoteFingerprint === resolution.quote.quoteFingerprint,
      );
      if (!customerClaimLocalQuoteFresh) {
        setQuoteError(null);
        void setLocalCartQuote(identity, cart, resolution.quote).catch((error) => {
          const message = error instanceof Error ? error.message : "PROMOTION_CLAIM_LOCAL_QUOTE_WRITE_FAILED";
          if (!message.includes("POS_SALE_QUOTE_CART_CHANGED")) setQuoteError(friendlyError(message));
        });
        return;
      }
      if (online && !quoteBusy) void refreshServerQuote();
      return;
    }

    if (claimSession.credentialCode) {
      if (online && !serverQuoteFresh && !quoteBusy) void refreshServerQuote();
      return;
    }

    let resolution;
    try {
      resolution = resolveLocalPromotionsV1({
        cart,
        promotions: promotionRuntime.runtime.promotions.filter((promotion) => promotion.activation_code !== "CLAIM_CODE"),
        promotionRuntimeRevision: promotionRuntime.runtime.revision,
      });
    } catch (error) {
      setQuoteError(friendlyError(error instanceof Error ? error.message : "LOCAL_PROMOTION_RESOLUTION_FAILED"));
      return;
    }

    if (resolution.requiresOnlineResolution && online) {
      if (!serverQuoteFresh && !quoteBusy) void refreshServerQuote();
      return;
    }
    if (localQuoteFresh) return;

    setQuoteError(null);
    void setLocalCartQuote(identity, cart, resolution.quote).catch((error) => {
      const message = error instanceof Error ? error.message : "LOCAL_PROMOTION_QUOTE_WRITE_FAILED";
      if (!message.includes("POS_SALE_QUOTE_CART_CHANGED")) setQuoteError(friendlyError(message));
    });
  }, [
    cart,
    claimSession.credentialCode,
    claimSession.customerClaim,
    claimSession.evaluation?.claim_instance_id,
    identity,
    online,
    promotionRuntime.runtime.promotions,
    promotionRuntime.runtime.revision,
    promotionRuntime.status,
    quoteBusy,
    quoteFresh,
    refreshServerQuote,
    sameScope,
  ]);

  const searchMembers = useCallback(async (searchText: string) => {
    if (!identity) return;
    setMemberLoading(true);
    setMemberError(null);
    try {
      setMembers(await searchIndexedMembers(identity, searchText, 20));
    } catch {
      setMemberError("อ่านรายชื่อสมาชิกในเครื่องไม่สำเร็จ กรุณาลองใหม่");
    } finally {
      setMemberLoading(false);
    }
  }, [identity]);

  async function chooseMember(member: PosSaleMember) {
    if (!identity || !cart) return;
    await setLocalCartMember(identity, cart, {
      customerId: member.customer_id,
      membershipId: member.membership_id,
      customerCode: member.customer_code,
      customerName: member.customer_name,
      phone: member.phone,
    });
    setMemberSignupOpen(false);
    setMemberOpen(false);
  }

  async function clearMember() {
    if (!identity || !cart) return;
    await setLocalCartMember(identity, cart, null);
    setMemberSignupOpen(false);
    setMemberOpen(false);
  }

  function openSignup() {
    setSignupNickname("");
    setSignupPhone(/^\d{8,15}$/.test(normalizeIdentifier(memberSearch).replace(/\D/g, "")) ? memberSearch : "");
    setSignupTermsAccepted(false);
    setSignupError(null);
    setMemberSignupOpen(true);
  }

  async function submitMemberSignup(event: React.FormEvent) {
    event.preventDefault();
    if (!identity || !terminal?.deviceSecret || !session?.sessionToken || !canSignupMember) return;

    const nickname = signupNickname.trim();
    const phone = signupPhone.trim();
    const normalizedPhone = normalizeIdentifier(phone);
    if (!nickname) {
      setSignupError("กรุณากรอกชื่อเล่น");
      return;
    }
    if (!/^\+?\d{8,15}$/.test(normalizedPhone)) {
      setSignupError("กรุณากรอกเบอร์โทรให้ถูกต้อง");
      return;
    }
    if (!signupTermsAccepted) {
      setSignupError("กรุณายืนยันว่าลูกค้ารับทราบเงื่อนไขสมาชิกและความเป็นส่วนตัว");
      return;
    }
    if (!online) {
      setSignupError("สมัครสมาชิกใหม่ต้อง Online แต่การค้นสมาชิกเดิมยังใช้ Local ได้ตามปกติ");
      return;
    }

    setSignupBusy(true);
    setSignupError(null);
    try {
      const localExact = (await searchIndexedMembers(identity, phone, 10)).filter((member) => {
        if (normalizeIdentifier(member.phone) === normalizedPhone) return true;
        return member.identifiers.some(
          (identifier) => identifier.identifier_type === "PHONE" && normalizeIdentifier(identifier.display_value) === normalizedPhone,
        );
      });
      if (localExact.length) {
        setMembers(localExact);
        setMemberSearch(phone);
        setMemberSignupOpen(false);
        setMemberError("เบอร์นี้มีสมาชิกอยู่แล้ว กรุณาเลือกชื่อที่ถูกต้องจากรายการ");
        return;
      }

      const result = await signupPosMember({
        identity,
        deviceSecret: terminal.deviceSecret,
        sessionToken: session.sessionToken,
        nickname,
        phone,
        termsAccepted: true,
        requestKey: `member-signup:${crypto.randomUUID()}`,
      });
      await chooseMember(result.member);
    } catch (error) {
      setSignupError(memberSignupError(error instanceof Error ? error.message : "CUSTOMER_MEMBER_POS_SIGNUP_FAILED"));
    } finally {
      setSignupBusy(false);
    }
  }

  async function toggleClaim(promotionVersionId: string) {
    if (!identity || !cart) return;
    const selected = cart.claimedPromotionVersionIds.includes(promotionVersionId);
    if (!selected) clearPromotionClaimSession();
    const next = selected ? [] : [promotionVersionId];
    await setLocalCartPromotionClaims(identity, cart, next);
    setPromotionOpen(false);
  }

  async function clearStaffClaims() {
    if (!identity || !cart || !cart.claimedPromotionVersionIds.length) return;
    await setLocalCartPromotionClaims(identity, cart, []);
    setPromotionOpen(false);
  }

  async function applyCoupon(event: React.FormEvent) {
    event.preventDefault();
    const code = couponInput.trim().toUpperCase();
    if (!code || !online || !identity || !cart?.lines.length) return;
    if (cart.claimedPromotionVersionIds.length) {
      await setLocalCartPromotionClaims(identity, cart, []);
    }
    clearPromotionClaimSession();
    setPromotionClaimCredential(code);
    await refreshServerQuote();
  }

  function clearCoupon() {
    clearPromotionClaimSession();
    setQuoteError(null);
  }

  const appliedAuto = useMemo(
    () => (quote?.appliedPromotions ?? []).filter((item) => item.activationCode === "AUTO"),
    [quote?.appliedPromotions],
  );

  const selectedStaffClaims = useMemo(() => {
    const selectedIds = new Set(cart?.claimedPromotionVersionIds ?? []);
    return promotionRuntime.runtime.promotions.filter(
      (promotion) => promotion.activation_code === "STAFF_CLAIM" && selectedIds.has(promotion.promotion_version_id),
    );
  }, [cart?.claimedPromotionVersionIds, promotionRuntime.runtime.promotions]);

  if (!mountNode || !terminal || !identity || !actor || !cart || !sameScope) return null;

  const promotionRuntimeReady = promotionRuntime.status !== "loading" && promotionRuntime.status !== "error";
  const onlineResolution = Boolean(quote?.requiresOnlineResolution || claimSession.credentialCode);
  const claimEvaluation = claimSession.evaluation;
  const claimName = claimEvaluation?.claim_definition?.display_name ?? "คูปองลูกค้า";
  const claimApplied = claimEvaluation?.validation_state !== "INVALID" && claimEvaluation?.quote_outcome === "APPLIED";
  const claimBlocked = claimEvaluation?.validation_state !== "INVALID" && claimEvaluation?.quote_outcome === "BLOCKED";
  const claimInvalid = claimEvaluation?.validation_state === "INVALID";
  const staffClaimLabel = selectedStaffClaims.length === 1
    ? selectedStaffClaims[0].policy_name
    : selectedStaffClaims.length > 1
      ? `${selectedStaffClaims.length} สิทธิ์ที่เลือก`
      : null;
  const memberSearchDigits = normalizeIdentifier(memberSearch).replace(/\D/g, "");
  const last4Search = /^\d{4}$/.test(memberSearchDigits);

  return (
    <>
      {createPortal(
        <div className="sale-context-bar">
          <button
            type="button"
            className={`sale-context-chip ${cart.selectedMember ? "sale-context-chip--active" : ""}`}
            onClick={() => {
              setMemberOpen(true);
              setMemberSignupOpen(false);
              setMemberSearch("");
              setMemberError(null);
              void searchMembers("");
            }}
          >
            <span aria-hidden="true">♙</span>
            <b>{cart.selectedMember ? cart.selectedMember.customerName : "สมาชิก"}</b>
          </button>
          <button
            type="button"
            className={`sale-context-chip ${(quote?.promotionDiscountTotal ?? 0) > 0 || cart.claimedPromotionVersionIds.length || claimSession.credentialCode ? "sale-context-chip--active" : ""}`}
            disabled={!cart.lines.length || !promotionRuntimeReady}
            onClick={() => setPromotionOpen(true)}
            title={staffClaimLabel ? "กดเพื่อเปลี่ยนหรือจัดการสิทธิ์" : undefined}
          >
            <span aria-hidden="true">%</span>
            <b>{quoteBusy && !claimSession.customerClaim ? "ตรวจสิทธิ์ออนไลน์…" : staffClaimLabel ?? ((quote?.promotionDiscountTotal ?? 0) > 0 ? `ลด ${money(quote!.promotionDiscountTotal, quote!.currencyCode)}` : "สิทธิ์/โปรโมชั่น")}</b>
          </button>
          {selectedStaffClaims.length ? <button type="button" className="sale-context-retry" onClick={() => void clearStaffClaims()}>{selectedStaffClaims.length > 1 ? "ล้างสิทธิ์ที่เลือก" : "เอาสิทธิ์ออก"}</button> : null}
          {quoteError && onlineResolution ? <button type="button" className="sale-context-retry" onClick={() => void refreshServerQuote()}>ตรวจใหม่</button> : null}
        </div>,
        mountNode,
      )}

      {memberOpen ? createPortal(
        <div className="sale-context-backdrop" role="presentation">
          <section className="sale-context-modal" role="dialog" aria-modal="true" aria-labelledby="sale-member-title">
            <header>
              <div><small>LOCAL MEMBER INDEX</small><h2 id="sale-member-title">{memberSignupOpen ? "สมัครสมาชิกใหม่" : "เลือกสมาชิก"}</h2></div>
              <button type="button" onClick={() => { setMemberSignupOpen(false); setMemberOpen(false); }}>×</button>
            </header>

            {memberSignupOpen ? (
              <form className="sale-member-signup" onSubmit={(event) => void submitMemberSignup(event)}>
                <label>
                  <span>ชื่อเล่น</span>
                  <input autoFocus value={signupNickname} onChange={(event) => setSignupNickname(event.target.value)} maxLength={80} placeholder="เช่น โอม" />
                </label>
                <label>
                  <span>เบอร์โทร</span>
                  <input value={signupPhone} onChange={(event) => setSignupPhone(event.target.value)} inputMode="tel" autoComplete="tel" placeholder="08XXXXXXXX" />
                </label>
                <label className="sale-member-terms">
                  <input type="checkbox" checked={signupTermsAccepted} onChange={(event) => setSignupTermsAccepted(event.target.checked)} />
                  <span>ลูกค้ายืนยันสมัครสมาชิกและรับทราบเงื่อนไขสมาชิก / ความเป็นส่วนตัว</span>
                </label>
                {signupError ? <div className="sale-context-error">{signupError}</div> : null}
                {!online ? <div className="sale-context-empty">ค้นสมาชิกเดิมยังใช้ได้จากเครื่อง แต่การสร้างสมาชิกใหม่ต้อง Online เพื่อบันทึกที่ Customer Core</div> : null}
                <div className="sale-member-signup-actions">
                  <button type="button" className="sale-member-clear" onClick={() => { setMemberSignupOpen(false); setSignupError(null); }}>ย้อนกลับ</button>
                  <button type="submit" className="sale-member-signup-submit" disabled={!online || signupBusy}>{signupBusy ? "กำลังบันทึก…" : "สมัครและใช้กับบิลนี้"}</button>
                </div>
              </form>
            ) : (
              <>
                <form className="sale-member-search" onSubmit={(event) => { event.preventDefault(); void searchMembers(memberSearch.trim()); }}>
                  <input
                    autoFocus
                    value={memberSearch}
                    onChange={(event) => { const value = event.target.value; setMemberSearch(value); void searchMembers(value); }}
                    placeholder="ค้นด้วยชื่อ หรือเบอร์โทร 4 ตัวท้าย"
                  />
                  <button type="submit">ค้นหา</button>
                </form>
                {last4Search && members.length > 1 ? <div className="sale-context-empty">พบ {members.length} รายชื่อที่ใช้เลขท้าย {memberSearchDigits} กรุณาเลือกชื่อที่ถูกต้อง</div> : null}
                {cart.selectedMember ? <button type="button" className="sale-member-clear" onClick={() => void clearMember()}>ขายแบบไม่ระบุสมาชิก</button> : null}
                {memberError ? <div className="sale-context-error">{memberError}</div> : null}
                <div className="sale-member-results">
                  {members.length ? members.map((member) => (
                    <button type="button" key={member.membership_id} onClick={() => void chooseMember(member)}>
                      <span><strong>{member.customer_name}</strong><small>{member.phone ? `เบอร์ลงท้าย ${phoneLast4(member.phone)}` : ""}</small></span>
                      <b>เลือก</b>
                    </button>
                  )) : <div className="sale-context-empty">{memberLoading ? "กำลังอ่านข้อมูลในเครื่อง…" : "ไม่พบสมาชิกที่ตรงกับคำค้นในข้อมูลล่าสุดของเครื่อง"}</div>}
                </div>
                {canSignupMember ? (
                  <div className="sale-member-signup-entry">
                    <div><strong>ยังไม่มีสมาชิก?</strong><small>สมัครด้วยชื่อเล่น + เบอร์โทร ใช้ Customer Core เดิม</small></div>
                    <button type="button" disabled={!online} onClick={openSignup}>สมัครสมาชิกใหม่</button>
                  </div>
                ) : null}
              </>
            )}
          </section>
        </div>,
        document.body,
      ) : null}

      {promotionOpen ? createPortal(
        <div className="sale-context-backdrop" role="presentation">
          <section className="sale-context-modal" role="dialog" aria-modal="true" aria-labelledby="sale-promo-title">
            <header><div><small>PUBLISHED PROMOTION</small><h2 id="sale-promo-title">สิทธิ์ / โปรโมชั่น</h2></div><button type="button" onClick={() => setPromotionOpen(false)}>×</button></header>
            {quote?.requiresOnlineResolution && !online ? <div className="sale-context-error">มีสิทธิ์ที่ต้องตรวจ Online ก่อนใช้ในบิลนี้</div> : null}
            {promotionRuntime.runtimeError ? <div className="sale-context-error">{friendlyError(promotionRuntime.runtimeError)}</div> : null}
            {quoteError ? <div className="sale-context-error">{quoteError}</div> : null}

            <section className="sale-promo-section">
              <div className="sale-promo-heading"><strong>รหัสคูปองลูกค้า</strong><small>ONLINE_REQUIRED · ตรวจ Core เฉพาะเมื่อกรอกรหัส</small></div>
              <form className="sale-member-search" onSubmit={(event) => void applyCoupon(event)}>
                <input
                  value={couponInput}
                  onChange={(event) => setCouponInput(event.target.value.toUpperCase())}
                  placeholder="กรอกรหัสคูปอง"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={128}
                />
                <button type="submit" disabled={!online || quoteBusy || !couponInput.trim()}>{quoteBusy ? "กำลังตรวจ…" : "ตรวจรหัส"}</button>
              </form>
              {!online ? <div className="sale-context-error">คูปองต้อง Online เพื่อยืนยันสิทธิ์ แต่ Cart และโปรโมชั่น Local-capable ยังใช้งานต่อได้</div> : null}
              {claimSession.credentialCode && !claimEvaluation && !quoteBusy ? <div className="sale-context-empty">ยังไม่ได้ผลตรวจรหัส กรุณากดตรวจใหม่</div> : null}
              {claimInvalid ? <div className="sale-context-error">{claimReason(claimEvaluation?.reason_code)}</div> : null}
              {claimApplied ? (
                <div className="sale-promo-applied">
                  <span><b>{claimName}</b><small>รหัสถูกต้อง · ส่วนลดถูกใช้กับบิลนี้แล้ว{claimEvaluation?.credential_hint ? ` · ลงท้าย ${claimEvaluation.credential_hint}` : ""}</small></span>
                  <strong>−{money(Number(claimEvaluation?.applied_discount_amount ?? 0), quote?.currencyCode)}</strong>
                </div>
              ) : null}
              {claimBlocked ? (
                <div className="sale-promo-applied">
                  <span><b>{claimName}</b><small>รับสิทธิ์แล้ว ≠ ใช้ส่วนลดแล้ว · {claimReason(claimEvaluation?.reason_code)}</small></span>
                  <strong>ยังไม่ลด</strong>
                </div>
              ) : null}
              {claimSession.credentialCode ? <button type="button" className="sale-member-clear" onClick={clearCoupon}>ล้างรหัสคูปองออกจากบิล</button> : null}
            </section>

            <section className="sale-promo-section">
              <div className="sale-promo-heading"><strong>โปรโมชั่นอัตโนมัติ</strong><small>OFFLINE_SAFE คำนวณจาก Published Local Runtime ทันที</small></div>
              {appliedAuto.length ? appliedAuto.map((promotion) => (
                <div className="sale-promo-applied" key={promotion.promotionVersionId}><span><b>{promotion.policyName}</b><small>{promotion.offlineCapability === "OFFLINE_SAFE" ? "ใช้จาก Local Runtime" : "Promotion Core ใช้แล้ว"}</small></span><strong>−{money(promotion.appliedDiscountAmount, quote?.currencyCode)}</strong></div>
              )) : <div className="sale-context-empty">ยังไม่มีโปรโมชั่น AUTO ที่เข้าเงื่อนไขในบิลนี้</div>}
            </section>

            <section className="sale-promo-section">
              <div className="sale-promo-heading"><strong>สิทธิ์ที่พนักงานเลือกได้</strong><small>Published STAFF_CLAIM ตาม Runtime Capability</small></div>
              {quote?.staffClaimOptions.length ? quote.staffClaimOptions.map((promotion) => {
                const selected = cart.claimedPromotionVersionIds.includes(promotion.promotionVersionId);
                const needsOnline = promotion.offlineCapability === "ONLINE_REQUIRED";
                return <button type="button" className={`sale-promo-option ${selected ? "sale-promo-option--selected" : ""}`} disabled={needsOnline && !online} key={promotion.promotionVersionId} onClick={() => void toggleClaim(promotion.promotionVersionId)}><span><b>{promotion.policyName}</b><small>{promotion.policyCode}{needsOnline ? " · ต้อง Online" : " · ใช้ Local ได้"}</small></span><strong>{selected ? "เอาออก" : "เลือก"}</strong></button>;
              }) : <div className="sale-context-empty">ยังไม่มีโปรโมชั่นแบบพนักงานเลือกที่ Published</div>}
            </section>

            <footer><span>ยอดก่อนโปร {money(quote?.prePromotionTotal ?? cart.lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0), quote?.currencyCode)}</span><strong>ยอดสุทธิ {money(quote?.postPromotionTotal ?? cart.lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0), quote?.currencyCode)}</strong></footer>
          </section>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
